import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StockUseReport = () => {
  const [issues, setIssues] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredIssues, setFilteredIssues] = useState([]);

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    try {
      const response = await apiClient.get('/api/inventory/stock-issues');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setIssues(data);
      setFilteredIssues(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleFilter = () => {
    if (!startDate || !endDate) {
      setFilteredIssues(issues);
      return;
    }
    const filtered = issues.filter(issue => {
      const issueDate = new Date(issue.issue_date);
      return issueDate >= new Date(startDate) && issueDate <= new Date(endDate);
    });
    setFilteredIssues(filtered);
  };

  const calculateTotalValue = () => {
    return filteredIssues.reduce((sum, issue) => sum + issue.total_value, 0);
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Stock Usage Report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button onClick={handleFilter} className="w-full">Filter</Button>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Total Issues</div>
                <div className="text-2xl font-bold">{filteredIssues.length}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Value</div>
                <div className="text-2xl font-bold text-blue-600">₹{calculateTotalValue().toFixed(2)}</div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Issue #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Issued To</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Purpose</th>
                  <th className="text-left p-3">Total Value</th>
                  <th className="text-left p-3">Issued By</th>
                </tr>
              </thead>
              <tbody>
                {filteredIssues.length === 0 ? (
                  <tr><td colSpan="7" className="text-center p-4 text-gray-500">No stock issues found.</td></tr>
                ) : (
                  filteredIssues.map((issue) => (
                    <tr key={issue.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{issue.issue_number}</td>
                      <td className="p-3">{formatDate(issue.issue_date)}</td>
                      <td className="p-3">{issue.issued_to}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{issue.issued_to_type}</span>
                      </td>
                      <td className="p-3">{issue.purpose || 'N/A'}</td>
                      <td className="p-3 font-medium">₹{issue.total_value}</td>
                      <td className="p-3">{issue.issued_by}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StockUseReport;